import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SettingSetView } from '@lemoncloud/codes-goods-api';

export interface SettingSetViewWithArrValue extends SettingSetView {
    arrValue?: string | number;
}

interface SettingSetMap {
    [scope: string]: SettingSetViewWithArrValue[];
}

type SettingSetValue = string | number | boolean | string[] | number[] | null | undefined;

enum RequiredErrorCase {
    EmptyString = 'emptyString', // string === ""
    Empty = 'empty', // number === null or NaN
    EmptyStringArray = 'emptyStringArray', // string[] === [] or undefined
    EmptyNumberArray = 'emptyNumberArray', // number[] === [] or undefined
}

export const useServiceSettings = (settings: SettingSetView[] = []) => {
    const [settingSetMap, setSettingSetMap] = useState<SettingSetMap>({});
    const initialSettingSetMap = useRef<SettingSetMap>({});
    const isInitialized = useRef(false);
    const scopes = useMemo(() => Object.keys(settingSetMap).sort(), [settingSetMap]);

    const formatMap = (settings: SettingSetView[]): SettingSetMap => {
        const newSettingSetMap: SettingSetMap = {};

        settings.forEach(setting => {
            const scope = setting.scope;

            if (!scope) {
                return;
            }

            const value = setSettingValue(setting);

            if (!newSettingSetMap[scope]) {
                newSettingSetMap[scope] = [
                    {
                        ...setting,
                        value,
                        ...(setting.isArray && (setting.type === 'string' ? { arrValue: '' } : { arrValue: 0 })),
                    },
                ];
                return;
            }

            newSettingSetMap[scope].push({
                ...setting,
                value,
                ...(setting.isArray && (setting.type === 'string' ? { arrValue: '' } : { arrValue: 0 })),
            });
        });

        return newSettingSetMap;
    };

    const setSettingValue = (setting: SettingSetView) => {
        if (setting.value === undefined || setting.value === null) {
            if (setting.isArray) {
                return [];
            }

            if (setting.defaultValue !== undefined && setting.defaultValue !== null) {
                return setting.defaultValue;
            }

            switch (setting.type) {
                case 'boolean':
                    return false;
                case 'float':
                case 'number':
                    return 0;
                case 'string':
                    return '';
                default:
                    return '';
            }
        }

        if (Array.isArray(setting.value)) {
            return [...setting.value] as string[] | number[];
        }

        return setting.value;
    };

    const setInitialSettingSetMap = (settings: SettingSetView[] | undefined) => {
        if (!settings) {
            return;
        }

        const fullSettingSetMap = formatMap(settings);

        initialSettingSetMap.current = { ...fullSettingSetMap };
        setSettingSetMap({ ...fullSettingSetMap });
    };

    const replaceValue = useCallback((setting: SettingSetView, idx: number, value: SettingSetValue) => {
        const scope = setting.scope;

        if (!scope) {
            return;
        }

        setSettingSetMap(prev => {
            if (!prev[scope]) {
                return { ...prev };
            }

            return {
                ...prev,
                [scope]: prev[scope].map((s, settingIndex) => {
                    if (settingIndex === idx) {
                        return { ...s, value };
                    }
                    return s;
                }),
            };
        });
    }, []);

    const addValue = useCallback((setting: SettingSetView, idx: number, value: string | number) => {
        const scope = setting.scope;

        if (!scope || !setting.isArray) {
            return;
        }

        setSettingSetMap(prev => {
            if (!prev[scope]) {
                return { ...prev };
            }

            return {
                ...prev,
                [scope]: prev[scope].map((s, settingIndex) => {
                    if (settingIndex === idx) {
                        const prevValue = s.value as (string | number)[];
                        return {
                            ...s,
                            value: [...prevValue, s.type === 'string' ? value : Number(value)],
                            arrValue: '',
                        };
                    }
                    return s;
                }),
            };
        });
    }, []);

    const addArrValue = useCallback((setting: SettingSetView, idx: number, value: string | number) => {
        const scope = setting.scope;

        if (!scope || !setting.isArray) {
            return;
        }

        setSettingSetMap(prev => {
            if (!prev[scope]) {
                return { ...prev };
            }

            return {
                ...prev,
                [scope]: prev[scope].map((s, settingIndex) => {
                    if (settingIndex === idx) {
                        return { ...s, arrValue: value };
                    }
                    return s;
                }),
            };
        });
    }, []);

    const removeValue = useCallback((setting: SettingSetView, idx: number, valueIndex: number) => {
        const scope = setting.scope;

        if (!scope || !setting.isArray) {
            return;
        }

        setSettingSetMap(prev => {
            if (!prev[scope]) {
                return { ...prev };
            }

            return {
                ...prev,
                [scope]: prev[scope].map((s, settingIndex) => {
                    if (settingIndex === idx) {
                        const prevValue = [...(s.value as (string | number)[])];
                        prevValue.splice(valueIndex, 1);
                        return { ...s, value: prevValue };
                    }
                    return s;
                }),
            };
        });
    }, []);

    const getChangedSettings = useCallback(() => {
        if (!settings || settings.length === 0) {
            return [];
        }

        const changedSettings = Object.values(settingSetMap)
            .map(settingsArr => {
                return settingsArr.filter(setting => {
                    const scope = setting.scope;

                    if (!scope || !settings) {
                        return false;
                    }

                    const prevSetting = settings
                        .map(s => ({ ...s, value: setSettingValue(s) }))
                        .find(prevSetting => prevSetting.scope === setting.scope && prevSetting.name === setting.name);

                    if (!prevSetting) {
                        return false;
                    }

                    if (setting.isArray) {
                        const settingValue = [...(setting.value as unknown[])];
                        const prevSettingValue = [...(prevSetting.value as unknown[])];

                        const settingValueStr = settingValue.sort().toString();
                        const prevSettingValueStr = prevSettingValue.sort().toString();

                        return settingValueStr !== prevSettingValueStr;
                    }

                    return setting.value !== prevSetting.value;
                });
            })
            .flatMap(arr => arr);

        return changedSettings;
    }, [settings, settingSetMap]);

    const isAllRequired = useCallback(() => {
        for (let i = 0; i < scopes.length; i++) {
            for (let j = 0; j < settingSetMap[scopes[i]].length; j++) {
                const setting = settingSetMap[scopes[i]][j];

                if (setting.isRequired) {
                    if (setting.isArray) {
                        const value = setting.value as unknown[];

                        if (value.length === 0) {
                            return false;
                        }
                    }

                    if (setting.value === undefined || setting.value === null) {
                        return false;
                    }

                    if (setting.type === 'string') {
                        const value = setting.value as string;

                        if (value === '') {
                            return false;
                        }
                    }
                }
            }
        }

        return true;
    }, [scopes, settingSetMap]);

    const isChanged = useMemo(() => {
        const pureInitialSettingMap = scopes
            .map(scope => initialSettingSetMap.current[scope] || [])
            .flat()
            .map(({ arrValue, ...rest }) => rest);
        const pureSettingSetMap = scopes
            .map(scope => settingSetMap[scope] || [])
            .flat()
            .map(({ arrValue, ...rest }) => rest);

        return JSON.stringify(pureInitialSettingMap) !== JSON.stringify(pureSettingSetMap);
    }, [settingSetMap, scopes]);

    const getErrorCase = useCallback((setting: SettingSetView): RequiredErrorCase | null => {
        if (!setting.isRequired) {
            return null;
        }

        if (setting.isArray) {
            const value = setting.value as unknown[];

            if (value.length === 0) {
                return setting.type === 'number'
                    ? RequiredErrorCase.EmptyNumberArray
                    : RequiredErrorCase.EmptyStringArray;
            }
        }

        if (setting.type === 'string' && !setting.value) {
            return RequiredErrorCase.EmptyString;
        }

        if (setting.value === undefined || setting.value === null) {
            return RequiredErrorCase.Empty;
        }

        return null;
    }, []);

    // Reset hook state when dialog closes
    const reset = useCallback(() => {
        isInitialized.current = false;
        setSettingSetMap({});
        initialSettingSetMap.current = {};
    }, []);

    // Use a stable key to detect actual settings changes (not just reference changes)
    const settingsKey = useMemo(
        () =>
            settings.length > 0
                ? JSON.stringify(settings.map(s => ({ scope: s.scope, name: s.name, value: s.value })))
                : '',
        [settings]
    );

    useEffect(() => {
        // Only initialize once when settings are first loaded, or when settingsKey actually changes
        if (settings.length > 0 && !isInitialized.current) {
            setInitialSettingSetMap([...settings]);
            isInitialized.current = true;
        }
    }, [settingsKey]);

    return {
        settingSetMap,
        scopes,
        replaceValue,
        setInitialSettingSetMap,
        addValue,
        addArrValue,
        removeValue,
        getChangedSettings,
        isAllRequired,
        getErrorCase,
        isChanged,
        reset,
    };
};
